import { z } from 'zod'
import {
	DynamicSectionType,
	LeaderboardSectionType,
	LeaderboardUIType,
	GameType,
	GameListSectionType,
} from '@/schema'

const gameListSectionSchema = z
	.object({
		title: z.string().min(1, 'กรุณากรอกชื่อหัวข้อ'),
		link: z.string().optional(),
		gameType: z.nativeEnum(GameType).optional(),
		gameListType: z.nativeEnum(GameListSectionType),
		filterProviderIds: z.array(z.string()).optional(),
		gameIds: z.array(z.string()).optional(),
	})
	.superRefine((data, ctx) => {
		if (data.gameListType === 'CUSTOM') {
			if (!data.gameIds || data.gameIds.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'กรุณาเลือกเกมอย่างน้อย 1 เกม',
					path: ['gameIds'],
				})
			}
			if (data.gameIds && data.gameIds.length > 10) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'เลือกเกมได้สูงสุด 10 เกม',
					path: ['gameIds'],
				})
			}
		} else {
			if (!data.gameType) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'กรุณาเลือกประเภทเกม',
					path: ['gameType'],
				})
			}
		}
	})

const lottoAnnouncementSectionSchema = z.object({})

const leaderboardSectionSchema = z
	.object({
		title: z.string().min(1, 'กรุณากรอกชื่อหัวข้อ'),
		leaderboardType: z.nativeEnum(LeaderboardSectionType),
		uiType: z.nativeEnum(LeaderboardUIType),
		limit: z.number().optional(),
	})
	.superRefine((data, ctx) => {
		// For horizontal UI, limit is required and must be between 5-100
		if (data.uiType === LeaderboardUIType.HORIZONTAL) {
			if (!data.limit) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'กรุณากรอกจำนวน',
					path: ['limit'],
				})
			} else if (data.limit < 5 || data.limit > 100) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'จำนวนต้องอยู่ระหว่าง 5-100',
					path: ['limit'],
				})
			}
		}
	})

const gameSuggestionSectionSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกชื่อหัวข้อ'),
	limit: z
		.number()
		.min(1, 'จำนวนต้องอย่างน้อย 1')
		.max(6, 'จำนวนสูงสุด 6')
		.optional(),
})

const footballMatchesSectionSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกชื่อหัวข้อ'),
	gameId: z.string().optional(),
	showLive: z.boolean().optional(),
	showUpcoming: z.boolean().optional(),
	showFinished: z.boolean().optional(),
})

export const dynamicSectionFormSchema = z
	.object({
		name: z.string().min(1, 'กรุณากรอกชื่อ Section'),
		type: z.nativeEnum(DynamicSectionType),
		gameListSection: gameListSectionSchema.optional(),
		lottoAnnouncementSection: lottoAnnouncementSectionSchema.optional(),
		leaderboardSection: leaderboardSectionSchema.optional(),
		gameSuggestionSection: gameSuggestionSectionSchema.optional(),
		footballMatchesSection: footballMatchesSectionSchema.optional(),
	})
	.superRefine((data, ctx) => {
		if (data.type === DynamicSectionType.GAME_LIST && !data.gameListSection) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'กรุณากรอกข้อมูลการตั้งค่าการโชว์เกมให้ครบถ้วน',
				path: ['gameListSection'],
			})
		}
		if (
			data.type === DynamicSectionType.LOTTO_ANNOUNCEMENT &&
			!data.lottoAnnouncementSection
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'กรุณากรอกข้อมูลการตั้งค่าการโชว์ประกาศหวยให้ครบถ้วน',
				path: ['lottoAnnouncementSection'],
			})
		}
		if (
			data.type === DynamicSectionType.LEADERBOARD &&
			!data.leaderboardSection
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'กรุณากรอกข้อมูลการตั้งค่าการโชว์อันดับให้ครบถ้วน',
				path: ['leaderboardSection'],
			})
		}
		if (
			data.type === DynamicSectionType.GAME_SUGGESTION &&
			!data.gameSuggestionSection
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'กรุณากรอกข้อมูลการตั้งค่าการโชว์เกมแนะนำให้ครบถ้วน',
				path: ['gameSuggestionSection'],
			})
		}
		if (
			data.type === DynamicSectionType.FOOTBALL_MATCHES &&
			!data.footballMatchesSection
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'กรุณากรอกข้อมูลการตั้งค่าการโชว์ฟุตบอลให้ครบถ้วน',
				path: ['footballMatchesSection'],
			})
		}
	})

export type DynamicSectionFormValues = z.infer<typeof dynamicSectionFormSchema>
